(() => {
  'use strict';
  if (!window.customElements || customElements.get('nmn-reference-timeline')) return;

  class ReferenceSection extends HTMLElement {
    connectedCallback() {
      this.onSectionLoad = (event) => {
        if (event.target.contains(this)) this.start();
      };
      this.onSectionUnload = (event) => {
        if (event.target.contains(this)) this.stop();
      };
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      document.addEventListener('shopify:section:unload', this.onSectionUnload);
      this.start();
    }
    disconnectedCallback() {
      this.stop();
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
    }
  }

  class ReferenceTimeline extends ReferenceSection {
    start() {
      if (this.started) return;
      this.started = true;
      this.rows = Array.from(this.querySelectorAll('[data-checkpoint]'));
      this.panels = Array.from(this.querySelectorAll('[data-checkpoint-panel]'));
      if (!this.rows.length) return;
      this.onClick = (event) => {
        const button = event.target.closest('[data-checkpoint-button]');
        if (button && this.contains(button)) this.activate(button.closest('[data-checkpoint]').dataset.checkpoint);
      };
      this.onBlockSelect = (event) => {
        const row = this.rows.find((item) => item.dataset.checkpoint === event.detail.blockId);
        if (row) this.activate(row.dataset.checkpoint);
      };
      this.addEventListener('click', this.onClick);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.classList.add('is-enhanced');
      this.activate(this.rows[0].dataset.checkpoint);
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.onMediaPlay = (event) => {
        if (this.mobile.matches && event.target.tagName === 'VIDEO') event.target.pause();
      };
      this.onMobileChange = () => {
        this.querySelectorAll('video').forEach((video) => {
          if (this.mobile.matches) video.pause();
          else if (video.autoplay) video.play()?.catch(() => {});
        });
      };
      this.addEventListener('play', this.onMediaPlay, true);
      this.mobile.addEventListener('change', this.onMobileChange);
      this.onMobileChange();
      if ('IntersectionObserver' in window) {
        this.onResize = () => {
          this.observer?.disconnect();
          // Pixel margins keep the trigger at 60% of height at every aspect ratio.
          const height = window.innerHeight;
          const top = Math.round(height * 0.6);
          this.observer = new IntersectionObserver((entries) => {
            const current = entries.find((entry) => entry.isIntersecting);
            if (current) this.activate(current.target.dataset.checkpoint);
          }, { rootMargin: `-${top}px 0px -${Math.max(0, height - top - 1)}px 0px`, threshold: 0 });
          this.rows.forEach((row) => this.observer.observe(row));
        };
        window.addEventListener('resize', this.onResize);
        this.onResize();
      }
    }
    activate(id) {
      this.rows.forEach((row) => {
        const selected = row.dataset.checkpoint === id;
        row.classList.toggle('is-active', selected);
        row.querySelector('[data-checkpoint-button]').setAttribute('aria-pressed', String(selected));
      });
      this.panels.forEach((panel) => { panel.hidden = panel.dataset.checkpointPanel !== id; });
    }
    stop() {
      this.observer?.disconnect();
      window.removeEventListener('resize', this.onResize);
      this.removeEventListener('play', this.onMediaPlay, true);
      this.mobile?.removeEventListener('change', this.onMobileChange);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.classList.remove('is-enhanced');
      this.rows?.forEach((row) => {
        row.classList.remove('is-active');
        row.querySelector('[data-checkpoint-button]').setAttribute('aria-pressed', 'false');
      });
      this.panels?.forEach((panel) => { panel.hidden = false; });
      this.started = false;
    }
  }

  class ReferenceAgeChart extends ReferenceSection {
    start() {
      if (this.started) return;
      this.started = true;
      this.line = this.querySelector('.nmn-reference-study-trend');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onMotionChange = () => {
        if (this.motion.matches) {
          this.animation?.cancel();
          this.observer?.disconnect();
          this.revealed = true;
        }
      };
      this.motion.addEventListener('change', this.onMotionChange);
      if (!this.line || this.revealed || this.motion.matches || !('IntersectionObserver' in window) || !this.line.animate) return;
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.revealed = true;
        if (this.motion.matches) return;
        const length = this.line.getTotalLength();
        const duration = 1400;
        const marks = Array.from(this.querySelectorAll('.nmn-reference-plot-marks > *'));
        marks.forEach((mark) => { mark.style.opacity = '0'; });
        this.animation = this.line.animate([
          { strokeDasharray: String(length), strokeDashoffset: String(length) },
          { strokeDasharray: String(length), strokeDashoffset: '0' }
        ], { duration, easing: 'cubic-bezier(.2,.7,.2,1)' });
        // Each marker lights up as the line reaches its own x position.
        const box = this.line.getBBox();
        this.markTimers = marks.map((mark) => {
          const m = mark.getBBox();
          const at = box.width ? Math.min(1, Math.max(0, (m.x + m.width / 2 - box.x) / box.width)) : 1;
          return window.setTimeout(() => { mark.style.opacity = '1'; }, duration * at * 0.9);
        });
        // A fifth of the chart counts as "in view" while it is still a sliver at
        // the bottom of the screen, so the line finished drawing before anyone
        // had looked at it. Wait until most of the chart is up and clear of the
        // bottom edge, which is when a reader has actually arrived at it.
      }, { threshold: 0.45, rootMargin: '0px 0px -10% 0px' });
      this.observer.observe(this.querySelector('svg'));
    }
    stop() {
      this.observer?.disconnect();
      this.animation?.cancel();
      this.markTimers?.forEach(window.clearTimeout);
      this.motion?.removeEventListener('change', this.onMotionChange);
      this.started = false;
    }
  }

  customElements.define('nmn-reference-timeline', ReferenceTimeline);
  customElements.define('nmn-reference-age-chart', ReferenceAgeChart);
})();

// Lottie checkpoints. The player is 164 KB, so it is fetched only when a
// checkpoint that uses one is close to the viewport, and only once per page.
(() => {
  const holders = document.querySelectorAll('[data-lottie-src]');
  if (!holders.length || !('IntersectionObserver' in window)) return;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let loading = null;
  const player = () => {
    if (!loading) {
      loading = new Promise((resolve, reject) => {
        const tag = document.createElement('script');
        tag.src = window.themeLottieSrc || '';
        if (!tag.src) { reject(new Error('no player url')); return; }
        tag.onload = () => resolve(window.lottie);
        tag.onerror = reject;
        document.head.appendChild(tag);
      });
    }
    return loading;
  };
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const node = entry.target;
      observer.unobserve(node);
      if (motion.matches) return;           // a still first frame is enough here
      player().then((lottie) => {
        if (!lottie || node.dataset.lottieReady) return;
        node.dataset.lottieReady = '1';
        lottie.loadAnimation({
          container: node, renderer: 'svg', loop: true, autoplay: true,
          path: node.dataset.lottieSrc,
        });
      }).catch(() => { /* the panel keeps its empty frame rather than erroring */ });
    });
  }, { rootMargin: '200px' });
  holders.forEach((node) => observer.observe(node));
})();
